// @spec GHG-STR-012
/**
 * TypeScript types for GHG OCR document processing
 */

export type GhgDocumentType =
  | 'electricity_bill'
  | 'gas_bill'
  | 'fuel_receipt'
  | 'water_bill'
  | 'supplier_invoice'
  | 'freight_invoice'
  | 'travel_invoice'
  | 'spend_report'
  | 'leased_assets_data';

export type GhgOcrStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface GhgExtractedData {
  supplier?: string | null;
  accountNumber?: string | null;
  billingPeriodStart?: string | null;
  billingPeriodEnd?: string | null;
  consumption?: number | null;
  consumptionUnit?: string | null;
  totalAmount?: number | null;
  currency?: string | null;
  meterReadingStart?: number | null;
  meterReadingEnd?: number | null;
  address?: string | null;
  suggestedActivityGroupId?: string | null;
  suggestedActivityCategory?: string | null;
  fuelType?: string | null;
  receiptDate?: string | null;
  vehicleRegistration?: string | null;
  unitPrice?: number | null;
  confidence: number;
}

export interface GhgOcrUploadResponse {
  id: string;
  fileName: string;
  fileSize: number;
  documentType: GhgDocumentType;
  ocrStatus: GhgOcrStatus;
  uploadedAt: string;
  facilityId?: string | null;
  reportingPeriodId?: string | null;
}

export interface GhgOcrStatusResponse {
  id: string;
  ocrStatus: GhgOcrStatus;
  extractedData?: GhgExtractedData | null;
  processingError?: string | null;
  processedAt?: string | null;
  requiresReview?: boolean;
  reviewReason?: string | null;
}

export interface GhgOcrConfirmRequest {
  scope: 1 | 2 | 3;
  activityCategory: string;
  activityType: string;
  activityGroupId?: string | undefined;
  facilityId?: string | undefined;
  scope3Category?: number | undefined;
  scope1Context?: Record<string, unknown> | undefined;
  scope2Context?: Record<string, unknown> | undefined;
  reportingPeriodId: string;
  quantity: number;
  unit: string;
  notes?: string | undefined;
  storeAsEvidence?: boolean | undefined;
  confirmLowConfidence?: boolean | undefined;
}

export interface GhgOcrConfirmResponse {
  activityDataId: string;
  evidenceId?: string | null;
  message: string;
}

export interface GhgOcrRetryResponse {
  id: string;
  ocrStatus: GhgOcrStatus;
  message: string;
}

export interface GhgOcrUploadInput {
  file: File;
  documentType: GhgDocumentType;
  facilityId?: string | undefined;
  reportingPeriodId?: string | undefined;
}

export interface LeasedAssetRowConfirmation {
  rowIndex: number;
  assetName: string;
  assetType: string;
  energyConsumption?: number | undefined;
  energyUnit?: string | undefined;
  energySource?: string | undefined;
  floorArea?: number | undefined;
  areaUnit?: string | undefined;
  spendAmount?: number | undefined;
  currency?: string | undefined;
  country?: string | undefined;
  location?: string | undefined;
  notes?: string | undefined;
}

export interface GhgOcrBatchConfirmRequest {
  scope3Category: 8 | 13;
  reportingPeriodId: string;
  facilityId?: string | undefined;
  assets: LeasedAssetRowConfirmation[];
  storeAsEvidence?: boolean | undefined;
}

export interface CreatedAssetEntry {
  rowIndex: number;
  activityDataId: string;
  assetName: string;
  co2eKg?: number | null;
  calculationApproach?: string | null;
  estimationMethod?: string | null;
  dataQuality?: string | null;
}

export interface GhgOcrBatchConfirmResponse {
  createdCount: number;
  skippedCount: number;
  entries: CreatedAssetEntry[];
  errors: Array<{ rowIndex: number; assetName: string; error: string }>;
  evidenceId?: string | null;
}

export const GHG_DOCUMENT_TYPE_OPTIONS = [
  { value: 'electricity_bill' as const, label: 'Electricity Bill' },
  { value: 'gas_bill' as const, label: 'Gas Bill' },
  { value: 'fuel_receipt' as const, label: 'Fuel Receipt' },
  { value: 'water_bill' as const, label: 'Water Bill' },
  { value: 'supplier_invoice' as const, label: 'Supplier Invoice (Scope 3)' },
  { value: 'freight_invoice' as const, label: 'Freight Invoice (Scope 3)' },
  { value: 'travel_invoice' as const, label: 'Travel Invoice (Scope 3)' },
  { value: 'spend_report' as const, label: 'Spend Report (Scope 3)' },
  { value: 'leased_assets_data' as const, label: 'Leased Assets Data (Excel/PDF)' },
];

export const GHG_DOCUMENT_TYPE_DESCRIPTIONS: Record<GhgDocumentType, string> = {
  electricity_bill: 'Upload electricity utility bills to extract kWh consumption',
  gas_bill: 'Upload gas utility bills to extract natural gas consumption',
  fuel_receipt: 'Upload fuel receipts to extract diesel, petrol, or LPG purchases',
  water_bill: 'Upload water bills to extract water consumption (for reference)',
  supplier_invoice: 'Upload supplier invoices for Scope 3 spend and quantity extraction',
  freight_invoice: 'Upload freight invoices/manifests for transportation extraction',
  travel_invoice: 'Upload travel invoices or itineraries for business-travel extraction',
  spend_report: 'Upload spend reports (including .xlsx) for Scope 3 category ingestion',
  leased_assets_data:
    'Upload leased asset schedules (Excel/PDF) to extract energy, area, or spend data for multiple assets',
};

export const GHG_DOCUMENT_TYPE_ICONS: Record<GhgDocumentType, string> = {
  electricity_bill: 'Zap',
  gas_bill: 'Flame',
  fuel_receipt: 'Fuel',
  water_bill: 'Droplet',
  supplier_invoice: 'FileSpreadsheet',
  freight_invoice: 'Truck',
  travel_invoice: 'Plane',
  spend_report: 'FileBarChart',
  leased_assets_data: 'Building2',
};

export const OCR_CONFIDENCE_THRESHOLDS = {
  HIGH: 0.8,
  MEDIUM: 0.5,
  LOW: 0.3,
} as const;

export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' | 'very_low' {
  if (confidence >= OCR_CONFIDENCE_THRESHOLDS.HIGH) return 'high';
  if (confidence >= OCR_CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium';
  if (confidence >= OCR_CONFIDENCE_THRESHOLDS.LOW) return 'low';
  return 'very_low';
}

export const ALLOWED_OCR_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.xlsx'];

export const ALLOWED_OCR_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const MAX_OCR_FILE_SIZE = 20 * 1024 * 1024;

export function validateOcrFile(file: File): string[] {
  const errors: string[] = [];

  if (file.size > MAX_OCR_FILE_SIZE) {
    const maxMb = String(MAX_OCR_FILE_SIZE / (1024 * 1024));
    errors.push(`File size exceeds maximum allowed (${maxMb}MB)`);
  }

  if (file.size === 0) {
    errors.push('File is empty');
  }

  const extension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
  if (!ALLOWED_OCR_EXTENSIONS.includes(extension)) {
    errors.push('Invalid file type. Accepted types: PDF, PNG, JPG, JPEG, XLSX');
  }

  return errors;
}
